import React, { FormEvent, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import {
    useForceShipBundleMutation,
    useGetUshipQuery,
} from "../uship/ushipApi";
import {
    BundleScanResult,
    isBundleScanResult,
    Uship,
} from "../uship/types";
import { Button, Container, Form, Modal, Stack } from "react-bootstrap";
import uLab_Circle_RGB from '../../assets/images/uLab_Circle_RGB.png';
import uLab_Logo_RGB from '../../assets/images/uLab_Logo_RGB.png';
import '../../theme/components/card.css'
import '../../theme/components/button.css'
import LogoutButton from "./LogoutButton";

type BundleNotice = {
    title: string;
    body: string;
    onClose: () => void;
};

const BUNDLE_COMPLETE_PREFIX = 'Bundle complete';

export const OrderEntry = () => {


    const [orderNumber, setOrderNumber] = useState('');
    const [submittedOrderNumber, setSubmittedOrderNumber] = useState('');
    const [showPleaseWait, setShowPleaseWait] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const [showModal, setShowModal] = useState(false);
    const [showAlreadyShippedModal, setShowAlreadyShippedModal] = useState(false);
    const [bundleNotice, setBundleNotice] = useState<BundleNotice | null>(null);
    const [partialConfirm, setPartialConfirm] = useState<BundleScanResult | null>(null);
    const navigate = useNavigate()
    // currentData (not data) is undefined while a new query is in flight,
    // so the effect won't re-process the previous response when a new scan
    // is submitted. Using `data` here would cause the prior popup to flash
    // each time a new order is scanned.
    const { currentData: ushipData, isFetching, isLoading: isUshipLoading, isError: isUshipError, error: ushipError } = useGetUshipQuery(submittedOrderNumber, { skip: !submittedOrderNumber });
    const [forceShipBundle, { isLoading: isForcing }] = useForceShipBundleMutation();

    useEffect(() => {
        const checkFocus = () => {
            if (inputRef.current && document.activeElement !== inputRef.current) {
                inputRef.current.focus();
            }
        }

        document.addEventListener('click', checkFocus)
        return () => document.removeEventListener('click', checkFocus)
    }, [])

    const returnToScanning = () => {
        setBundleNotice(null);
        setPartialConfirm(null);
        setOrderNumber('');
        setSubmittedOrderNumber('');
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // printZpl sends a single ZPL document to the label printer via the
    // Electron bridge. Errors are swallowed (logged) so a printer hiccup
    // never blocks the operator's workflow; the popup still shows.
    const printZpl = async (zpl: string, label: string) => {
        if (!zpl) return;
        if (!(window as any).electronAPI) {
            console.error(`electronAPI is not available; cannot print ${label}`);
            return;
        }
        try {
            const status = await (window as any).electronAPI.printSilently(zpl);
            if (!status?.successful) {
                console.error(`${label} print failed:`, status?.simpleError);
            }
        } catch (err) {
            console.error(`Error printing ${label}:`, err);
        }
    };



    const handleOnSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        setShowPleaseWait(true)
        // const orderNumber = orderNumberText.trim()

        if (orderNumber.length < 1) {
            console.log(`odd.  handleOnSubmit for orderIdInput was called with a blank orderNumber - ${orderNumber}.`)
            setShowPleaseWait(false)
            setShowModal(true);
            return
        }

        // Defensively clear any popup state from a prior scan before
        // kicking off the new query.
        setBundleNotice(null);
        setPartialConfirm(null);

        console.log(`looking up order ${orderNumber}...`)
        setSubmittedOrderNumber(orderNumber);
    };

    useEffect(() => {
        if (!submittedOrderNumber) {
            return;
        }
        if (isUshipLoading || isFetching) {
            return;
        }
        if (isUshipError) {
            console.log(`Error fetching Uship data: ${typeof ushipError === 'object' ? (ushipError as any).message : ushipError}`)
            if (typeof ushipError === 'object' && ushipError && 'status' in ushipError && (ushipError as any).status === 404) {
                setShowModal(true);
            }
            setShowPleaseWait(false);
            return;
        }

        if (!ushipData) {
            return;
        }

        // Path 1: BundleScanResult envelope -> branch on action
        if (isBundleScanResult(ushipData)) {
            setShowPleaseWait(false);
            switch (ushipData.action) {
                case 'bundle_created': {
                    // Print manifest, brief delay, then per-order label;
                    // popup happens after both prints have been kicked
                    // off so the operator sees the right context.
                    (async () => {
                        if (ushipData.manifestZpl) {
                            await printZpl(ushipData.manifestZpl, 'manifest');
                            await new Promise((resolve) => setTimeout(resolve, 1000));
                        }
                        if (ushipData.orderLabelZpl) {
                            await printZpl(ushipData.orderLabelZpl, 'order label');
                        }
                        setBundleNotice({
                            title: 'Bundle Created',
                            body: ushipData.message,
                            onClose: returnToScanning,
                        });
                    })();
                    return;
                }
                case 'order_received': {
                    (async () => {
                        if (ushipData.orderLabelZpl) {
                            await printZpl(ushipData.orderLabelZpl, 'order label');
                        }
                        setBundleNotice({
                            title: 'Order Received',
                            body: ushipData.message,
                            onClose: returnToScanning,
                        });
                    })();
                    return;
                }
                case 'confirm_partial': {
                    setPartialConfirm(ushipData);
                    return;
                }
            }
        }

        // Path 2: UShipment - check for completed-bundle warning before navigating
        const uship = ushipData as Uship;
        const completedBundleWarning = (uship.warnings || []).find((w) =>
            w.startsWith(BUNDLE_COMPLETE_PREFIX),
        );
        if (completedBundleWarning) {
            setShowPleaseWait(false);
            // Fire-and-forget the per-order label print: the operator is
            // already standing at the box and the popup shouldn't wait
            // on the printer.
            if (uship.orderLabelZpl) {
                printZpl(uship.orderLabelZpl, 'order label').catch(() => {});
            }
            setBundleNotice({
                title: 'Bundle Complete',
                body: completedBundleWarning,
                onClose: () => {
                    setBundleNotice(null);
                    navigate(`/${uship.orderNumber}`);
                },
            });
            return;
        }

        // Path 3: existing solo-order behavior
        if (uship.orderNumber === submittedOrderNumber) {
            setShowPleaseWait(false)
            if (uship.status === 'shipment_created') {
                setShowAlreadyShippedModal(true);
            } else {
                navigate(`/${orderNumber}`);
            }
        }
    }, [submittedOrderNumber, isUshipLoading, isFetching, isUshipError, ushipData]);

    const OrderError = () => {

        return (
            <Modal show={showModal} onHide={() => { setShowModal(false); navigate('/'); setOrderNumber(''); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>Invalid order number</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { setShowModal(false); navigate('/'); setOrderNumber(''); }}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        )
    }

    const handleAlreadyShippedContinue = () => {
        setShowAlreadyShippedModal(false);
        navigate(`/${orderNumber}`);
    };

    const handleAlreadyShippedCancel = () => {
        setShowAlreadyShippedModal(false);
        setOrderNumber('');
        setSubmittedOrderNumber('');
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    const AlreadyShippedWarning = () => {
        return (
            <Modal 
                show={showAlreadyShippedModal} 
                onHide={handleAlreadyShippedCancel}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header closeButton style={{ borderBottom: '2px solid #f0ad4e' }}>
                    <Modal.Title style={{ color: '#856404', fontWeight: 600 }}>
                        Order Already Shipped
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '1.5rem' }}>
                    <p style={{ fontSize: '1.1rem', marginBottom: '0.75rem' }}>
                        <strong>{orderNumber}</strong> has already been shipped.
                    </p>
                    <p style={{ marginBottom: 0 }}>
                        You can reprint the label or review the shipment details. No new shipment will be created.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleAlreadyShippedCancel}>
                        Go Back
                    </Button>
                    <Button className="button" onClick={handleAlreadyShippedContinue}>
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>
        )
    }

    const BundleNoticeModal = () => {
        if (!bundleNotice) return null;
        return (
            <Modal
                show={true}
                onHide={bundleNotice.onClose}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>{bundleNotice.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '1.5rem' }}>
                    <p style={{ fontSize: '1.1rem', marginBottom: 0 }}>
                        {bundleNotice.body}
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <Button className="button" onClick={bundleNotice.onClose}>
                        Continue
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };

    const handleShipPartial = async () => {
        if (!partialConfirm) return;
        const barcode = partialConfirm.bundle.barcode;
        try {
            const result = await forceShipBundle(barcode).unwrap();
            if (result) {
                setPartialConfirm(null);
                navigate(`/${barcode}`);
            }
        } catch (err) {
            console.error('Error force-shipping bundle:', err);
        }
    };

    const PartialBundleConfirmModal = () => {
        if (!partialConfirm) return null;
        const missing = partialConfirm.missingOrders || [];
        return (
            <Modal
                show={true}
                onHide={returnToScanning}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header closeButton style={{ borderBottom: '2px solid #f0ad4e' }}>
                    <Modal.Title style={{ color: '#856404', fontWeight: 600 }}>
                        Bundle Incomplete
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '1.5rem' }}>
                    <p style={{ fontSize: '1.1rem', marginBottom: '0.75rem' }}>
                        {partialConfirm.message}
                    </p>
                    {missing.length > 0 && (
                        <>
                            <p style={{ marginBottom: '0.25rem', fontWeight: 600 }}>Missing orders:</p>
                            <ul style={{ marginBottom: 0 }}>
                                {missing.map((o) => (
                                    <li key={o}>{o}</li>
                                ))}
                            </ul>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={returnToScanning} disabled={isForcing}>
                        Cancel
                    </Button>
                    <Button className="button" onClick={handleShipPartial} disabled={isForcing}>
                        {isForcing ? (
                            <>
                                <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                <span role="status">Preparing...</span>
                            </>
                        ) : 'Ship Anyway'}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    };


    const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOrderNumber(e.target.value);
        e.preventDefault();
    };

    return (
        <>

            <div className="bground-image" style={{ backgroundImage: `url(${uLab_Circle_RGB})` }}>
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <img src={uLab_Logo_RGB} alt="uShip" style={{ width: '100px', height: 'auto' }} />
                    <Container>
                        <OrderError />
                        <AlreadyShippedWarning />
                        <BundleNoticeModal />
                        <PartialBundleConfirmModal />
                    </Container>
                    <LogoutButton />
                </div>
                <div className='bground' style={{ flexGrow: 1, overflow: 'auto' }}>
                    <h5 className="card-title">Please Scan Order</h5>
                    <Form onSubmit={handleOnSubmit}>
                        <Stack direction="horizontal" gap={2}>
                            <Form.Group>
                                <Form.Control
                                    type="text"
                                    placeholder="Enter Order Id"
                                    value={orderNumber}
                                    onChange={handleOnChange}
                                    autoFocus
                                    ref={inputRef}
                                />
                            </Form.Group>
                            <div className="btn-hover">
                                <button className="btn btn-primary" type="submit" disabled={showPleaseWait}>
                                    {showPleaseWait ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm" aria-hidden="true"></span>
                                            <span role="status">Loading...</span>
                                        </>
                                    ) : 'Submit'}
                                </button>
                            </div>
                        </Stack>

                    </Form>
                </div>
            </div>
        </>
    )
}
